
import React from 'react';
import { Button } from '@/components/ui/button';
import { Save, RefreshCw, Edit, Eye, EyeOff } from 'lucide-react';
import { 
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
  SheetClose
} from '@/components/ui/sheet';
import { KpiItem } from '@/hooks/useKpiLayout';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';

interface KpiLayoutControlsProps {
  editMode: boolean;
  toggleEditMode: () => void;
  resetLayout: () => void;
  hiddenKpis: KpiItem[];
  toggleVisibility: (id: string) => void;
}

export const KpiLayoutControls: React.FC<KpiLayoutControlsProps> = ({
  editMode,
  toggleEditMode,
  resetLayout,
  hiddenKpis,
  toggleVisibility
}) => {
  const { toast } = useToast();
  
  const handleResetLayout = () => {
    resetLayout();
    toast({
      title: "Layout Reset",
      description: "Dashboard layout has been reset to default",
    });
  };

  const handleSaveLayout = () => {
    toggleEditMode();
    toast({
      title: "Layout Saved",
      description: "Your custom dashboard layout has been saved",
    });
  };

  return (
    <div className="flex justify-end items-center gap-2 mb-4">
      {editMode && hiddenKpis.length > 0 && (
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              Hidden KPIs
              <Badge variant="secondary" className="ml-1">{hiddenKpis.length}</Badge>
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Hidden KPI Cards</SheetTitle>
              <SheetDescription>
                Restore hidden KPI cards to your dashboard
              </SheetDescription>
            </SheetHeader>
            
            <div className="py-4 space-y-4">
              {hiddenKpis.map((kpi) => (
                <div 
                  key={kpi.id} 
                  className="flex items-center justify-between p-3 rounded-md border"
                >
                  <div className="flex items-center gap-2">
                    <div className={`p-1 rounded ${kpi.colorClass}`}>
                      {kpi.icon}
                    </div>
                    <span>{kpi.title}</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => toggleVisibility(kpi.id)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              
              {hiddenKpis.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No hidden KPIs
                </div>
              )}
            </div>
            
            <SheetFooter>
              <SheetClose asChild>
                <Button variant="outline">Close</Button>
              </SheetClose>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      )}
      
      {editMode && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleResetLayout}
          className="flex items-center gap-1"
        >
          <RefreshCw className="h-4 w-4" />
          Reset Layout
        </Button>
      )}
      
      <Button 
        variant={editMode ? "default" : "outline"}
        size="sm"
        onClick={editMode ? handleSaveLayout : toggleEditMode}
        className="flex items-center gap-1"
      >
        {editMode ? (
          <>
            <Save className="h-4 w-4" />
            Save Layout
          </>
        ) : (
          <>
            <Edit className="h-4 w-4" />
            Customize
          </>
        )}
      </Button>
    </div>
  );
};
